import { arrayOf, numberOf, objectOf, optionalBool, optionalText, textOf, type WireObject } from '../../../shared/api/wire'
import type { AqsiRecoverAction, AqsiSaleState, AqsiStateLine, AqsiStatePrimary } from '../model/aqsiStateTypes'

/** Вид конверта ответа `GET …/sales/{saleId}/aqsi-state`. */
export const AQSI_STATE_KIND = 'aqsi.sale.aqsi-state'

/** Коды основного действия, которые окно умеет выполнять или показывать (§3). */
const ACTIONS: ReadonlySet<string> = new Set<AqsiRecoverAction>([
  'resend',
  'close-found',
  'cancel-stuck-then-resend',
  'wait',
  'issue-on-device',
  'needs-human',
  'none',
])

const optionalObject = (value: unknown, field: string): WireObject | null =>
  value === null || value === undefined ? null : objectOf(value, field)

function optionalKopecks(value: unknown, field: string): number | null {
  if (value === null || value === undefined) return null
  const number = numberOf(value, field)
  return Number.isSafeInteger(number) ? number : null
}

function decodeLine(raw: unknown, index: number): AqsiStateLine {
  const field = `lines[${index}]`
  const line = objectOf(raw, field)
  return {
    key: textOf(line.key, `${field}.key`),
    // Слово состояния, которого окно не знает, — не ошибка: строка показывается знаком «не знаем» и текстом службы.
    state: optionalText(line.state, `${field}.state`) ?? 'unknown',
    text: optionalText(line.text, `${field}.text`) ?? '',
    readAt: optionalText(line.readAt, `${field}.readAt`),
    amountKopecks: optionalKopecks(line.amountKopecks, `${field}.amountKopecks`),
  }
}

function decodePrimary(raw: unknown): AqsiStatePrimary | null {
  const primary = optionalObject(raw, 'primary')
  if (primary === null) return null
  const rawCode = textOf(primary.code, 'primary.code')
  return {
    code: ACTIONS.has(rawCode) ? (rawCode as AqsiRecoverAction) : 'unknown',
    rawCode,
    label: optionalText(primary.label, 'primary.label'),
    // Доступность не названа — кнопка закрыта: действие на кассе не выполняется по догадке окна.
    enabled: optionalBool(primary.enabled, 'primary.enabled') ?? false,
    blockedReason: optionalText(primary.blockedReason, 'primary.blockedReason'),
    nextCheckAt: optionalText(primary.nextCheckAt, 'primary.nextCheckAt'),
  }
}

/**
 * Снимок «Проверка в AQSI» (`aqsi.sale.aqsi-state`). Терпимый к расширению договора: незнакомые поля пропускаются, незнакомые слова
 * состояния и коды действия не роняют ответ (строка — «не знаем», действие — `unknown` с кодом); неверный ТИП поля — ошибка на
 * границе, а не пустая панель.
 */
export function decodeAqsiSaleState(raw: unknown): AqsiSaleState {
  const data = objectOf(raw, 'проверка в AQSI')
  const lines = data.lines === null || data.lines === undefined ? [] : arrayOf(data.lines, 'lines').map(decodeLine)
  const secondary = optionalObject(data.secondary, 'secondary')
  const prefill = optionalObject(data.prefill, 'prefill')
  return {
    saleId: optionalText(data.saleId, 'saleId'),
    lines,
    cell: optionalText(data.cell, 'cell'),
    primary: decodePrimary(data.primary),
    // Служба не сказала — кнопка «Отложить продажу» остаётся: отказ, если он будет, придёт её словами.
    parkAllowed: optionalBool(secondary?.parkAllowed, 'secondary.parkAllowed') ?? true,
    prefill: {
      moneyTaken: optionalBool(prefill?.moneyTaken, 'prefill.moneyTaken'),
      fiscalized: optionalBool(prefill?.fiscalized, 'prefill.fiscalized'),
    },
    copyText: optionalText(data.copyText, 'copyText') ?? '',
  }
}

/**
 * Свежее состояние из отказа 409 `state-changed` (`receipt/recover`): служба кладёт его в тело отказа. Где именно — `state` или
 * `data`, — окно не угадывает молча: нет ни того, ни другого — `null`, и панель перечитывает состояние сама.
 */
export function stateOfConflict(details: Readonly<Record<string, unknown>> | undefined): AqsiSaleState | null {
  if (details === undefined) return null
  const found = details.state ?? details.data
  if (found === null || found === undefined || typeof found !== 'object') return null
  // Состояние в конверте (`{kind, data}`) — данные внутри него.
  const wrapped = (found as WireObject).kind === AQSI_STATE_KIND ? (found as WireObject).data : found
  try {
    return decodeAqsiSaleState(wrapped)
  } catch {
    return null
  }
}
